import type { ListItemProps } from '@mui/material';
import {
  Box,
  Grid2,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FieldArrayRenderProps } from 'formik';
import { useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Checkbox from '@components/Checkbox';
import ContentLoader from '@components/ContentLoader';
import { FormikCheckboxField } from '@components/FormikFields';

import type { AccessSchema, FormValues } from '@models/access-management';

import { PermissionControlScope } from 'generated/types';

const messages = defineMessages({
  recordsAccess: {
    id: 'accessManagement.recordsAccess',
    defaultMessage: 'Records Access',
  },
  featuresAccess: {
    id: 'accessManagement.featuresAccess',
    defaultMessage: 'Features/Settings Access',
  },
  view: {
    id: 'accessManagement.view',
    defaultMessage: 'View',
  },
  edit: {
    id: 'accessManagement.edit',
    defaultMessage: 'Edit',
  },
  viewAll: {
    id: 'accessManagement.viewAll',
    defaultMessage: 'View All',
  },
  editAll: {
    id: 'accessManagement.editAll',
    defaultMessage: 'Edit All',
  },
  allRecords: {
    id: 'accessManagement.allRecords',
    defaultMessage: 'All Records',
  },
  allFeatures: {
    id: 'accessManagement.allFeatures',
    defaultMessage: 'All Features/Settings',
  },
});

interface UserAccessFormProps {
  loading: boolean;
  arrayHelpers: FieldArrayRenderProps;
  index: number;
  controlSet: AccessSchema;
}

export default function UserAccessForm({
  loading,
  controlSet,
  index: accessIndex,
  arrayHelpers,
}: UserAccessFormProps) {
  const formik = useFormikContext<FormValues>();
  const { setFieldValue } = formik;
  const { formatMessage } = useIntl();
  const { palette } = useTheme();

  const { grants, name } = controlSet;
  const allViewable = grants.filter(
    ({ view }) => typeof view?.is_enabled === 'boolean'
  );
  const allEditable = grants.filter(
    ({ edit }) => typeof edit?.is_enabled === 'boolean'
  );

  const listItemProps = (args: {
    grantIndex: number | null;
    viewChecked?: boolean;
    editChecked?: boolean;
    lastItem?: boolean;
    allItems?: boolean;
  }): ListItemProps => {
    const { allItems, editChecked, viewChecked, lastItem, grantIndex } = args;
    return {
      secondaryAction: (
        <ContentLoader loading={loading}>
          <Stack direction="row" alignItems="flex-end">
            <Box
              sx={{
                width: '40px',
              }}
            >
              {typeof viewChecked === 'boolean' &&
                (allItems ? (
                  <Checkbox
                    checked={viewChecked}
                    label={
                      <Typography
                        variant="caption"
                        sx={{
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        {formatMessage(messages.viewAll)}
                      </Typography>
                    }
                    labelPlacement="top"
                    onChange={(event) => {
                      const newGrants: AccessSchema['grants'] =
                        controlSet.grants.map((grant) => {
                          const { checked } = event.target;
                          const { edit, view } = grant;
                          return {
                            ...grant,
                            view: view
                              ? {
                                  is_enabled: checked,
                                  scope: PermissionControlScope.read,
                                }
                              : view,
                            // If view all is unchecked, unselect all edit checkboxes
                            edit:
                              edit && checked === false
                                ? {
                                    is_enabled: checked,
                                    scope: PermissionControlScope.write,
                                  }
                                : edit,
                          };
                        });
                      arrayHelpers.replace(accessIndex, {
                        ...controlSet,
                        grants: newGrants,
                      });
                    }}
                  />
                ) : (
                  <FormikCheckboxField
                    label={
                      <Typography
                        variant="caption"
                        sx={{
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        {formatMessage(messages.viewAll)}
                      </Typography>
                    }
                    labelPlacement="top"
                    name={`access[${accessIndex}].grants[${grantIndex}].view.is_enabled`}
                    onChange={(checked) => {
                      const grant = grantIndex
                        ? controlSet.grants[grantIndex]
                        : null;
                      const isViewable = grant?.view;
                      const isEditable = grant?.edit;
                      if (isViewable) {
                        setFieldValue(
                          `access[${accessIndex}].grants[${grantIndex}].view.is_enabled`,
                          checked
                        );
                      }
                      // If view is unchecked, edit checkbox should be unchecked too
                      if (isEditable && checked === false) {
                        setFieldValue(
                          `access[${accessIndex}].grants[${grantIndex}].edit.is_enabled`,
                          checked
                        );
                      }
                    }}
                  />
                ))}
            </Box>
            <Box
              sx={{
                width: '40px',
              }}
            >
              {typeof editChecked === 'boolean' &&
                (allItems ? (
                  <Checkbox
                    checked={editChecked}
                    label={
                      <Typography
                        variant="caption"
                        sx={{
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        {formatMessage(messages.editAll)}
                      </Typography>
                    }
                    labelPlacement="top"
                    onChange={(event) => {
                      const newGrants: AccessSchema['grants'] =
                        controlSet.grants.map((grant) => {
                          const { checked } = event.target;
                          const { edit, view } = grant;
                          return {
                            ...grant,
                            view:
                              // If edit all is checked, select all view checkboxes
                              view && checked === true
                                ? {
                                    is_enabled: checked,
                                    scope: PermissionControlScope.read,
                                  }
                                : view,
                            edit: edit
                              ? {
                                  is_enabled: checked,
                                  scope: PermissionControlScope.write,
                                }
                              : edit,
                          };
                        });
                      arrayHelpers.replace(accessIndex, {
                        ...controlSet,
                        grants: newGrants,
                      });
                    }}
                  />
                ) : (
                  <FormikCheckboxField
                    label={
                      <Typography
                        variant="caption"
                        sx={{
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        {formatMessage(messages.edit)}
                      </Typography>
                    }
                    labelPlacement="top"
                    name={`access[${accessIndex}].grants[${grantIndex}].edit.is_enabled`}
                    onChange={(checked) => {
                      const grant = grantIndex
                        ? controlSet.grants[grantIndex]
                        : null;
                      const isViewable = grant?.view;
                      const isEditable = grant?.edit;
                      if (isEditable) {
                        setFieldValue(
                          `access[${accessIndex}].grants[${grantIndex}].edit.is_enabled`,
                          checked
                        );
                      }
                      // If edit is checked, then view should be checked too
                      if (isViewable && checked) {
                        setFieldValue(
                          `access[${accessIndex}].grants[${grantIndex}].view.is_enabled`,
                          checked
                        );
                      }
                    }}
                  />
                ))}
            </Box>
          </Stack>
        </ContentLoader>
      ),
      divider: Boolean(!lastItem),
      sx: {
        height: '70px',
        backgroundColor: allItems
          ? palette.grey.A200
          : palette.background.paper,
        paddingLeft: allItems ? 2 : 4,
        wordBreak: 'break-word',
      },
    };
  };

  return (
    <Grid2 size={{ xs: 12, sm: 6 }}>
      <Typography variant="subtitle2">
        {formatMessage(
          name === 'patient_acc_mgmt:records'
            ? messages.recordsAccess
            : messages.featuresAccess
        )}
      </Typography>
      <Paper>
        <List dense>
          <ListItem
            {...listItemProps({
              allItems: true,
              viewChecked: allViewable.every(({ view }) => view?.is_enabled),
              editChecked: allEditable.every(({ edit }) => edit?.is_enabled),
              grantIndex: null,
            })}
          >
            <ListItemText
              primary={formatMessage(
                name === 'patient_acc_mgmt:records'
                  ? messages.allRecords
                  : messages.allFeatures
              )}
            />
          </ListItem>
          {grants.map(
            ({ grantName, permission_control_id, edit, view }, grantIndex) => {
              return (
                <ListItem
                  key={permission_control_id}
                  {...listItemProps({
                    viewChecked: view?.is_enabled ?? undefined,
                    editChecked: edit?.is_enabled ?? undefined,
                    lastItem: grantIndex === grants.length - 1,
                    grantIndex,
                  })}
                >
                  <ListItemText
                    primary={formatMessage({
                      id: `accessManagement.${grantName}`,
                    })}
                    primaryTypographyProps={{
                      variant: 'subtitle2',
                    }}
                    sx={{ maxWidth: '80%' }}
                  />
                </ListItem>
              );
            }
          )}
        </List>
      </Paper>
    </Grid2>
  );
}
